import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { User, Lock, MapPin, Palette, Sun, Moon, Monitor, Save, Upload, Camera, Trash2, Bell, Calendar, Plane, CheckSquare, FolderKanban, Clock, MessageSquare, Trophy, Mail, Phone, Briefcase, X, Plus, Smartphone, BellRing } from 'lucide-react';

const SettingsPage = () => {
    const { user, api, refreshUser } = useAuth();
    const { theme, resolvedTheme, setTheme } = useTheme();
    const { isSupported: pushSupported, isSubscribed: pushSubscribed, permissionState, loading: pushLoading, subscribe: subscribePush, unsubscribe: unsubscribePush, testNotification } = usePushNotifications();
    const [loading, setLoading] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [profileImage, setProfileImage] = useState(user?.profile_image || null);
    const fileInputRef = useRef(null);
    const [notificationSettings, setNotificationSettings] = useState(null);
    const [savingNotifications, setSavingNotifications] = useState(false);
    const [sendingTestEmail, setSendingTestEmail] = useState(false);
    const [newSkill, setNewSkill] = useState('');
    
    const [profileData, setProfileData] = useState({
        first_name: user?.first_name || '',
        last_name: user?.last_name || '',
        weather_location: user?.weather_location || 'Vienna,AT',
        birthday: user?.birthday || '',
        profile_color: user?.profile_color || '#009dde',
        phone: user?.phone || '',
        location: user?.location || '',
        bio: user?.bio || '',
        skills: user?.skills || [],
        google_email: user?.google_email || ''
    });
    const [passwordData, setPasswordData] = useState({
        old_password: '',
        new_password: '',
        confirm_password: ''
    });

    // Fetch notification settings on mount
    useEffect(() => {
        const fetchNotificationSettings = async () => {
            try {
                const res = await api.get('/users/me/notification-settings');
                setNotificationSettings(res.data);
            } catch (error) {
                console.error('Failed to load notification settings');
            }
        };
        fetchNotificationSettings();
    }, [api]);

    const handleNotificationChange = async (key, value) => {
        // Optimistic update
        setNotificationSettings(prev => ({ ...prev, [key]: value }));
        
        try {
            await api.put('/users/me/notification-settings', { [key]: value });
        } catch (error) {
            // Revert on error
            setNotificationSettings(prev => ({ ...prev, [key]: !value }));
            toast.error('Fehler beim Speichern');
        }
    };

    const handleEnableAllNotifications = async () => {
        setSavingNotifications(true);
        try {
            const res = await api.post('/users/me/notification-settings/enable-all');
            setNotificationSettings(res.data);
            toast.success('Alle Benachrichtigungen aktiviert');
        } catch (error) {
            toast.error('Fehler beim Aktivieren');
        } finally {
            setSavingNotifications(false);
        }
    };

    const handleDisableAllNotifications = async () => {
        setSavingNotifications(true);
        try {
            const res = await api.post('/users/me/notification-settings/disable-all');
            setNotificationSettings(res.data);
            toast.success('Alle Benachrichtigungen deaktiviert');
        } catch (error) {
            toast.error('Fehler beim Deaktivieren');
        } finally {
            setSavingNotifications(false);
        }
    };

    const handleSendTestEmail = async () => {
        setSendingTestEmail(true);
        try {
            const res = await api.post('/test-email');
            if (res.data.status === 'success') {
                toast.success('Test-E-Mail gesendet! Überprüfe dein Postfach.');
            } else {
                toast.error(res.data.message || 'Fehler beim Senden');
            }
        } catch (error) {
            toast.error('E-Mail konnte nicht gesendet werden');
        } finally {
            setSendingTestEmail(false);
        }
    };

    const handleAddSkill = () => {
        if (newSkill.trim() && !profileData.skills.includes(newSkill.trim())) {
            setProfileData({
                ...profileData,
                skills: [...profileData.skills, newSkill.trim()]
            });
            setNewSkill('');
        }
    };

    const handleRemoveSkill = (skillToRemove) => {
        setProfileData({
            ...profileData,
            skills: profileData.skills.filter(s => s !== skillToRemove)
        });
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Bild zu groß (max. 5MB)');
            return;
        }
        
        setUploadingImage(true);
        const formData = new FormData();
        formData.append('file', file);
        
        try {
            const response = await api.post(`/system/users/${user.id}/profile-image`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setProfileImage(response.data.profile_image);
            toast.success('Profilbild aktualisiert! 📸');
            refreshUser();
        } catch (error) {
            toast.error('Fehler beim Hochladen');
        } finally {
            setUploadingImage(false);
        }
    };

    const handleRemoveImage = async () => {
        try {
            await api.put(`/users/${user.id}`, { profile_image: null });
            setProfileImage(null);
            toast.success('Profilbild entfernt');
            refreshUser();
        } catch (error) {
            toast.error('Fehler beim Entfernen');
        }
    };

    const handleSaveProfile = async () => {
        setLoading(true);
        try {
            await api.put(`/users/${user.id}`, profileData);
            toast.success('Profil aktualisiert');
            refreshUser();
        } catch (error) {
            toast.error('Fehler beim Speichern');
        } finally {
            setLoading(false);
        }
    };

    const handleChangePassword = async () => {
        if (passwordData.new_password !== passwordData.confirm_password) {
            toast.error('Passwörter stimmen nicht überein');
            return;
        }
        
        setLoading(true);
        try {
            await api.put('/auth/password', {
                old_password: passwordData.old_password,
                new_password: passwordData.new_password
            });
            toast.success('Passwort geändert');
            setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Ändern');
        } finally {
            setLoading(false);
        }
    };

    const getInitials = () => {
        return `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`.toUpperCase();
    };

    return (
        <div className="p-6 md:p-8 max-w-4xl mx-auto" data-testid="settings-page">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    ⚙️ Einstellungen
                </h1>
                <p className="text-muted-foreground">Verwalte dein Profil und Einstellungen</p>
            </div>

            <div className="space-y-6">
                {/* Profile Card */}
                <Card className="shadow-soft" data-testid="profile-card">
                    <CardHeader>
                        <div className="flex items-center gap-4">
                            {/* Profile Image with Upload */}
                            <div className="relative group">
                                <Avatar className="w-20 h-20 border-4 border-background shadow-lg">
                                    {profileImage ? (
                                        <AvatarImage src={profileImage} className="object-cover" />
                                    ) : null}
                                    <AvatarFallback 
                                        style={{ backgroundColor: profileData.profile_color }}
                                        className="text-white text-2xl font-semibold"
                                    >
                                        {getInitials()}
                                    </AvatarFallback>
                                </Avatar>
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={uploadingImage}
                                    className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                                >
                                    {uploadingImage ? (
                                        <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <Camera className="w-6 h-6 text-white" />
                                    )}
                                </button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    className="hidden"
                                />
                            </div>
                            <div className="flex-1">
                                <CardTitle>Profil</CardTitle>
                                <CardDescription>{user?.email}</CardDescription>
                                <div className="flex gap-2 mt-2">
                                    <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploadingImage}
                                    >
                                        <Upload className="w-3.5 h-3.5 mr-1.5" />
                                        {profileImage ? 'Ändern' : 'Hochladen'}
                                    </Button>
                                    {profileImage && (
                                        <Button 
                                            variant="ghost" 
                                            size="sm"
                                            onClick={handleRemoveImage}
                                            className="text-destructive hover:text-destructive"
                                        >
                                            <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                                            Entfernen
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Vorname</Label>
                                <Input
                                    value={profileData.first_name}
                                    onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                                    data-testid="first-name-input"
                                />
                            </div>
                            <div>
                                <Label>Nachname</Label>
                                <Input
                                    value={profileData.last_name}
                                    onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                                    data-testid="last-name-input"
                                />
                            </div>
                        </div>
                        <div>
                            <Label className="flex items-center gap-2">
                                <MapPin className="w-4 h-4" />
                                Wetter-Standort
                            </Label>
                            <Input
                                value={profileData.weather_location}
                                onChange={(e) => setProfileData({ ...profileData, weather_location: e.target.value })}
                                placeholder="z.B. Vienna,AT oder Berlin,DE"
                                data-testid="weather-location-input"
                            />
                        </div>
                        <div>
                            <Label>Geburtstag (MM-DD)</Label>
                            <Input
                                value={profileData.birthday}
                                onChange={(e) => setProfileData({ ...profileData, birthday: e.target.value })}
                                placeholder="z.B. 01-15 für 15. Januar"
                                data-testid="birthday-input"
                            />
                        </div>
                        <div>
                            <Label className="flex items-center gap-2">
                                <Palette className="w-4 h-4" />
                                Profilfarbe
                            </Label>
                            <div className="flex gap-2">
                                <Input
                                    type="color"
                                    value={profileData.profile_color}
                                    onChange={(e) => setProfileData({ ...profileData, profile_color: e.target.value })}
                                    className="w-12 h-10 p-1 cursor-pointer"
                                />
                                <Input
                                    value={profileData.profile_color}
                                    onChange={(e) => setProfileData({ ...profileData, profile_color: e.target.value })}
                                    data-testid="profile-color-input"
                                />
                            </div>
                        </div>

                        {/* Contact Info */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="flex items-center gap-2">
                                    <Phone className="w-4 h-4" />
                                    Telefon
                                </Label>
                                <Input
                                    value={profileData.phone}
                                    onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                                    placeholder="+43 660 1234567"
                                    data-testid="phone-input"
                                />
                            </div>
                            <div>
                                <Label className="flex items-center gap-2">
                                    <MapPin className="w-4 h-4" />
                                    Standort
                                </Label>
                                <Input
                                    value={profileData.location}
                                    onChange={(e) => setProfileData({ ...profileData, location: e.target.value })}
                                    placeholder="Wien, Österreich"
                                    data-testid="location-input"
                                />
                            </div>
                        </div>

                        {/* Google Login Email */}
                        <div>
                            <Label className="flex items-center gap-2">
                                <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                                Google Login E-Mail
                            </Label>
                            <Input
                                value={profileData.google_email}
                                onChange={(e) => setProfileData({ ...profileData, google_email: e.target.value })}
                                placeholder="deine.email@gmail.com"
                                type="email"
                                data-testid="google-email-input"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                Optional: Hinterlege eine Gmail-Adresse um dich alternativ mit Google anzumelden.
                            </p>
                        </div>

                        {/* Bio */}
                        <div>
                            <Label className="flex items-center gap-2">
                                <User className="w-4 h-4" />
                                Über mich
                            </Label>
                            <Textarea
                                value={profileData.bio}
                                onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                                placeholder="Kurze Beschreibung über dich..."
                                className="min-h-[80px]"
                                data-testid="bio-input"
                            />
                        </div>

                        {/* Skills */}
                        <div>
                            <Label className="flex items-center gap-2">
                                <Briefcase className="w-4 h-4" />
                                Skills & Expertise
                            </Label>
                            <div className="flex flex-wrap gap-2 mb-2">
                                {profileData.skills.map((skill, index) => (
                                    <Badge 
                                        key={index} 
                                        variant="secondary"
                                        className="flex items-center gap-1 pr-1"
                                    >
                                        {skill}
                                        <button
                                            onClick={() => handleRemoveSkill(skill)}
                                            className="ml-1 hover:bg-destructive/20 rounded-full p-0.5"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </Badge>
                                ))}
                            </div>
                            <div className="flex gap-2">
                                <Input
                                    value={newSkill}
                                    onChange={(e) => setNewSkill(e.target.value)}
                                    placeholder="Neuen Skill hinzufügen..."
                                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSkill())}
                                    data-testid="skill-input"
                                />
                                <Button 
                                    type="button" 
                                    variant="outline" 
                                    onClick={handleAddSkill}
                                    disabled={!newSkill.trim()}
                                >
                                    <Plus className="w-4 h-4" />
                                </Button>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                                z.B. Meta Ads, Videoschnitt, Design, Sales
                            </p>
                        </div>

                        <Button onClick={handleSaveProfile} disabled={loading} data-testid="save-profile-button">
                            <Save className="w-4 h-4 mr-2" />
                            Profil speichern
                        </Button>
                    </CardContent>
                </Card>

                {/* Theme Card */}
                <Card className="shadow-soft" data-testid="theme-card">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            {theme === 'system' ? <Monitor className="w-5 h-5" /> :
                             resolvedTheme === 'light' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                            Erscheinungsbild
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-3 gap-3">
                            <button
                                onClick={() => setTheme('system')}
                                className={`p-4 rounded-xl border-2 transition-all ${
                                    theme === 'system' ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'
                                }`}
                                data-testid="theme-system"
                            >
                                <Monitor className="w-6 h-6 mx-auto mb-2" />
                                <p className="text-sm font-medium">System</p>
                                <p className="text-xs text-muted-foreground">Automatisch</p>
                            </button>
                            <button
                                onClick={() => setTheme('light')}
                                className={`p-4 rounded-xl border-2 transition-all ${
                                    theme === 'light' ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'
                                }`}
                                data-testid="theme-light"
                            >
                                <Sun className="w-6 h-6 mx-auto mb-2" />
                                <p className="text-sm font-medium">Hell</p>
                                <p className="text-xs text-muted-foreground">Immer hell</p>
                            </button>
                            <button
                                onClick={() => setTheme('dark')}
                                className={`p-4 rounded-xl border-2 transition-all ${
                                    theme === 'dark' ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'
                                }`}
                                data-testid="theme-dark"
                            >
                                <Moon className="w-6 h-6 mx-auto mb-2" />
                                <p className="text-sm font-medium">Dunkel</p>
                                <p className="text-xs text-muted-foreground">Immer dunkel</p>
                            </button>
                        </div>
                        <p className="text-sm text-muted-foreground">
                            {theme === 'system' 
                                ? `Folgt deinen Systemeinstellungen (aktuell: ${resolvedTheme === 'dark' ? 'Dunkel' : 'Hell'})`
                                : `Manuell auf ${theme === 'dark' ? 'Dunkel' : 'Hell'} gesetzt`
                            }
                        </p>
                    </CardContent>
                </Card>

                {/* Password Card */}
                <Card className="shadow-soft" data-testid="password-card">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Lock className="w-5 h-5" />
                            Passwort ändern
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <Label>Aktuelles Passwort</Label>
                            <Input
                                type="password"
                                value={passwordData.old_password}
                                onChange={(e) => setPasswordData({ ...passwordData, old_password: e.target.value })}
                                data-testid="old-password-input"
                            />
                        </div>
                        <div>
                            <Label>Neues Passwort</Label>
                            <Input
                                type="password"
                                value={passwordData.new_password}
                                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                                data-testid="new-password-input"
                            />
                        </div>
                        <div>
                            <Label>Passwort bestätigen</Label>
                            <Input
                                type="password"
                                value={passwordData.confirm_password}
                                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                                data-testid="confirm-password-input"
                            />
                        </div>
                        <Button 
                            onClick={handleChangePassword} 
                            disabled={loading || !passwordData.old_password || !passwordData.new_password}
                            data-testid="change-password-button"
                        >
                            Passwort ändern
                        </Button>
                    </CardContent>
                </Card>

                {/* Notification Settings */}
                <Card className="shadow-soft" data-testid="notification-settings-card">
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-primary/10">
                                    <Bell className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <CardTitle>E-Mail Benachrichtigungen</CardTitle>
                                    <CardDescription>Wähle, welche E-Mails du erhalten möchtest</CardDescription>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleEnableAllNotifications}
                                    disabled={savingNotifications}
                                >
                                    Alle an
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleDisableAllNotifications}
                                    disabled={savingNotifications}
                                >
                                    Alle aus
                                </Button>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {notificationSettings && (
                            <>
                                {/* Calendar Notifications */}
                                <div className="space-y-3">
                                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                                        <Calendar className="w-4 h-4" />
                                        Kalender
                                    </div>
                                    <div className="space-y-2 pl-6">
                                        <div className="flex items-center justify-between">
                                            <Label className="cursor-pointer">Termineinladungen</Label>
                                            <Switch
                                                checked={notificationSettings.calendar_invitations}
                                                onCheckedChange={(v) => handleNotificationChange('calendar_invitations', v)}
                                            />
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <Label className="cursor-pointer">Terminerinnerungen</Label>
                                            <Switch
                                                checked={notificationSettings.calendar_reminders}
                                                onCheckedChange={(v) => handleNotificationChange('calendar_reminders', v)}
                                            />
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <Label className="cursor-pointer">Terminänderungen</Label>
                                            <Switch
                                                checked={notificationSettings.calendar_updates}
                                                onCheckedChange={(v) => handleNotificationChange('calendar_updates', v)}
                                            />
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <Label className="cursor-pointer">Terminabsagen</Label>
                                            <Switch
                                                checked={notificationSettings.calendar_cancellations}
                                                onCheckedChange={(v) => handleNotificationChange('calendar_cancellations', v)}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Vacation Notifications */}
                                <div className="space-y-3">
                                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                                        <Plane className="w-4 h-4" />
                                        Urlaub
                                    </div>
                                    <div className="space-y-2 pl-6">
                                        <div className="flex items-center justify-between">
                                            <Label className="cursor-pointer">Urlaubsanträge (für Vorgesetzte)</Label>
                                            <Switch
                                                checked={notificationSettings.vacation_requests}
                                                onCheckedChange={(v) => handleNotificationChange('vacation_requests', v)}
                                            />
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <Label className="cursor-pointer">Genehmigungen</Label>
                                            <Switch
                                                checked={notificationSettings.vacation_approvals}
                                                onCheckedChange={(v) => handleNotificationChange('vacation_approvals', v)}
                                            />
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <Label className="cursor-pointer">Ablehnungen</Label>
                                            <Switch
                                                checked={notificationSettings.vacation_rejections}
                                                onCheckedChange={(v) => handleNotificationChange('vacation_rejections', v)}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Task Notifications */}
                                <div className="space-y-3">
                                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                                        <CheckSquare className="w-4 h-4" />
                                        Tasks
                                    </div>
                                    <div className="space-y-2 pl-6">
                                        <div className="flex items-center justify-between">
                                            <Label className="cursor-pointer">Neue Zuweisungen</Label>
                                            <Switch
                                                checked={notificationSettings.task_assignments}
                                                onCheckedChange={(v) => handleNotificationChange('task_assignments', v)}
                                            />
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <Label className="cursor-pointer">Deadline-Erinnerungen</Label>
                                            <Switch
                                                checked={notificationSettings.task_reminders}
                                                onCheckedChange={(v) => handleNotificationChange('task_reminders', v)}
                                            />
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <Label className="cursor-pointer">Kommentare</Label>
                                            <Switch
                                                checked={notificationSettings.task_comments}
                                                onCheckedChange={(v) => handleNotificationChange('task_comments', v)}
                                            />
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <Label className="cursor-pointer">Tägliche Übersicht (E-Mail, 17:30)</Label>
                                                <p className="text-[11px] text-muted-foreground">Heute erledigt, überfällig, morgen fällig, Termine morgen</p>
                                            </div>
                                            <Switch
                                                checked={notificationSettings.daily_summary !== false}
                                                onCheckedChange={(v) => handleNotificationChange('daily_summary', v)}
                                                data-testid="notif-daily-summary"
                                            />
                                        </div>
                                        {user?.role === 'chef' && (
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <Label className="cursor-pointer">Tägliche Team-Übersicht (Admin)</Label>
                                                    <p className="text-[11px] text-muted-foreground">Alle Mitarbeiter auf einen Blick</p>
                                                </div>
                                                <Switch
                                                    checked={notificationSettings.daily_admin_summary !== false}
                                                    onCheckedChange={(v) => handleNotificationChange('daily_admin_summary', v)}
                                                    data-testid="notif-daily-admin-summary"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Project Notifications */}
                                <div className="space-y-3">
                                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                                        <FolderKanban className="w-4 h-4" />
                                        Projekte
                                    </div>
                                    <div className="space-y-2 pl-6">
                                        <div className="flex items-center justify-between">
                                            <Label className="cursor-pointer">Projekt-Zuweisungen</Label>
                                            <Switch
                                                checked={notificationSettings.project_assignments}
                                                onCheckedChange={(v) => handleNotificationChange('project_assignments', v)}
                                            />
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <Label className="cursor-pointer">Projekt-Deadlines</Label>
                                            <Switch
                                                checked={notificationSettings.project_deadlines}
                                                onCheckedChange={(v) => handleNotificationChange('project_deadlines', v)}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Time Tracking Notifications */}
                                <div className="space-y-3">
                                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                                        <Clock className="w-4 h-4" />
                                        Zeiterfassung
                                    </div>
                                    <div className="space-y-2 pl-6">
                                        <div className="flex items-center justify-between">
                                            <Label className="cursor-pointer">Wöchentlicher Zeitreport</Label>
                                            <Switch
                                                checked={notificationSettings.weekly_time_report}
                                                onCheckedChange={(v) => handleNotificationChange('weekly_time_report', v)}
                                            />
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <Label className="cursor-pointer">Monatliche Zusammenfassung</Label>
                                            <Switch
                                                checked={notificationSettings.monthly_time_summary !== false}
                                                onCheckedChange={(v) => handleNotificationChange('monthly_time_summary', v)}
                                            />
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <Label className="cursor-pointer">Fehlende Zeiteinträge</Label>
                                            <Switch
                                                checked={notificationSettings.missing_time_entries}
                                                onCheckedChange={(v) => handleNotificationChange('missing_time_entries', v)}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Chat Notifications */}
                                <div className="space-y-3">
                                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                                        <MessageSquare className="w-4 h-4" />
                                        Chat
                                    </div>
                                    <div className="space-y-2 pl-6">
                                        <div className="flex items-center justify-between">
                                            <Label className="cursor-pointer">Offline-Nachrichten</Label>
                                            <Switch
                                                checked={notificationSettings.offline_messages}
                                                onCheckedChange={(v) => handleNotificationChange('offline_messages', v)}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Challenge Notifications */}
                                <div className="space-y-3">
                                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                                        <Trophy className="w-4 h-4" />
                                        Challenges
                                    </div>
                                    <div className="space-y-2 pl-6">
                                        <div className="flex items-center justify-between">
                                            <Label className="cursor-pointer">Neue Challenges</Label>
                                            <Switch
                                                checked={notificationSettings.challenge_started}
                                                onCheckedChange={(v) => handleNotificationChange('challenge_started', v)}
                                            />
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <Label className="cursor-pointer">Abgeschlossene Challenges</Label>
                                            <Switch
                                                checked={notificationSettings.challenge_completed}
                                                onCheckedChange={(v) => handleNotificationChange('challenge_completed', v)}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Spesen / Formulare */}
                                <div className="space-y-3">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                                            <Mail className="w-4 h-4 text-green-600" />
                                        </div>
                                        <span className="font-medium">Spesen & Formulare</span>
                                    </div>
                                    <div className="space-y-2 pl-6">
                                        <div className="flex items-center justify-between">
                                            <Label className="cursor-pointer">In-App Benachrichtigungen</Label>
                                            <Switch
                                                checked={notificationSettings.expense_notifications !== false}
                                                onCheckedChange={(v) => handleNotificationChange('expense_notifications', v)}
                                            />
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <Label className="cursor-pointer">E-Mail bei Genehmigung/Ablehnung</Label>
                                            <Switch
                                                checked={notificationSettings.expense_email_notifications !== false}
                                                onCheckedChange={(v) => handleNotificationChange('expense_email_notifications', v)}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Test Email Button */}
                                <div className="pt-4 border-t">
                                    <Button
                                        variant="outline"
                                        onClick={handleSendTestEmail}
                                        disabled={sendingTestEmail}
                                        className="gap-2"
                                    >
                                        <Mail className="w-4 h-4" />
                                        {sendingTestEmail ? 'Wird gesendet...' : 'Test-E-Mail senden'}
                                    </Button>
                                    <p className="text-xs text-muted-foreground mt-2">
                                        Sendet eine Test-E-Mail an {user?.email}
                                    </p>
                                </div>
                            </>
                        )}
                    </CardContent>
                </Card>

                {/* PWA Push Notifications */}
                <Card className="shadow-soft" data-testid="push-notification-settings-card">
                    <CardHeader>
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-primary/10">
                                <Smartphone className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                                <CardTitle>Push Benachrichtigungen</CardTitle>
                                <CardDescription>Native Benachrichtigungen auf deinem Gerät</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {!pushSupported ? (
                            <div className="text-sm text-muted-foreground">
                                Push-Benachrichtigungen werden von diesem Browser nicht unterstützt.
                            </div>
                        ) : (
                            <>
                                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                                    <div className="flex items-center gap-3">
                                        <BellRing className="w-5 h-5 text-primary" />
                                        <div>
                                            <p className="font-medium">Push Benachrichtigungen</p>
                                            <p className="text-xs text-muted-foreground">
                                                {pushSubscribed 
                                                    ? 'Aktiv - Du erhältst Benachrichtigungen' 
                                                    : 'Inaktiv - Klicke zum Aktivieren'}
                                            </p>
                                        </div>
                                    </div>
                                    <Switch
                                        checked={pushSubscribed}
                                        disabled={pushLoading || permissionState === 'denied'}
                                        onCheckedChange={async (checked) => {
                                            if (checked) {
                                                const result = await subscribePush();
                                                if (result.success) {
                                                    toast.success('Push Benachrichtigungen aktiviert!');
                                                } else {
                                                    toast.error(result.error || 'Aktivierung fehlgeschlagen');
                                                }
                                            } else {
                                                const result = await unsubscribePush();
                                                if (result.success) {
                                                    toast.success('Push Benachrichtigungen deaktiviert');
                                                }
                                            }
                                        }}
                                    />
                                </div>

                                {permissionState === 'denied' && (
                                    <p className="text-sm text-destructive">
                                        Benachrichtigungen wurden im Browser blockiert. Bitte erlaube sie in den Browser-Einstellungen.
                                    </p>
                                )}

                                {pushSubscribed && user?.role === 'chef' && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="gap-2"
                                        onClick={async () => {
                                            const result = await testNotification();
                                            if (result.success) {
                                                toast.success('Test-Benachrichtigung gesendet!');
                                            } else {
                                                toast.error(result.error || 'Test fehlgeschlagen');
                                            }
                                        }}
                                    >
                                        <BellRing className="w-4 h-4" />
                                        Test-Benachrichtigung senden
                                    </Button>
                                )}

                                {/* Granular Push Settings */}
                                {pushSubscribed && notificationSettings && (
                                    <div className="space-y-4 pt-3 border-t">
                                        <p className="text-xs font-medium text-muted-foreground">Push-Benachrichtigungen fuer:</p>

                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <Label className="cursor-pointer flex items-center gap-2 text-sm">
                                                    <CheckSquare className="w-3.5 h-3.5 text-muted-foreground" />
                                                    Tasks
                                                </Label>
                                                <Switch
                                                    checked={notificationSettings.push_tasks !== false}
                                                    onCheckedChange={(v) => handleNotificationChange('push_tasks', v)}
                                                    data-testid="push-tasks-toggle"
                                                />
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <Label className="cursor-pointer flex items-center gap-2 text-sm">
                                                    <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                                                    Kalender
                                                </Label>
                                                <Switch
                                                    checked={notificationSettings.push_calendar !== false}
                                                    onCheckedChange={(v) => handleNotificationChange('push_calendar', v)}
                                                    data-testid="push-calendar-toggle"
                                                />
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <Label className="cursor-pointer flex items-center gap-2 text-sm">
                                                    <MessageSquare className="w-3.5 h-3.5 text-muted-foreground" />
                                                    Chat
                                                </Label>
                                                <Switch
                                                    checked={notificationSettings.push_chat !== false}
                                                    onCheckedChange={(v) => handleNotificationChange('push_chat', v)}
                                                    data-testid="push-chat-toggle"
                                                />
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <Label className="cursor-pointer flex items-center gap-2 text-sm">
                                                    <Plane className="w-3.5 h-3.5 text-muted-foreground" />
                                                    Urlaub
                                                </Label>
                                                <Switch
                                                    checked={notificationSettings.push_vacation !== false}
                                                    onCheckedChange={(v) => handleNotificationChange('push_vacation', v)}
                                                    data-testid="push-vacation-toggle"
                                                />
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <Label className="cursor-pointer flex items-center gap-2 text-sm">
                                                    <FolderKanban className="w-3.5 h-3.5 text-muted-foreground" />
                                                    Projekte
                                                </Label>
                                                <Switch
                                                    checked={notificationSettings.push_projects !== false}
                                                    onCheckedChange={(v) => handleNotificationChange('push_projects', v)}
                                                    data-testid="push-projects-toggle"
                                                />
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <Label className="cursor-pointer flex items-center gap-2 text-sm">
                                                    <Trophy className="w-3.5 h-3.5 text-muted-foreground" />
                                                    Challenges
                                                </Label>
                                                <Switch
                                                    checked={notificationSettings.push_challenges !== false}
                                                    onCheckedChange={(v) => handleNotificationChange('push_challenges', v)}
                                                    data-testid="push-challenges-toggle"
                                                />
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <Label className="cursor-pointer flex items-center gap-2 text-sm">
                                                    <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                                                    Formulare / Spesen
                                                </Label>
                                                <Switch
                                                    checked={notificationSettings.push_forms !== false}
                                                    onCheckedChange={(v) => handleNotificationChange('push_forms', v)}
                                                    data-testid="push-forms-toggle"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </CardContent>
                </Card>

                {/* Account Info */}
                <Card className="shadow-soft">
                    <CardHeader>
                        <CardTitle>Account-Informationen</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Rolle</span>
                                <span className="font-medium capitalize">{user?.role}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Wochenstunden</span>
                                <span className="font-medium">{user?.weekly_hours}h</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Urlaubstage gesamt</span>
                                <span className="font-medium">{user?.total_vacation_days} Tage</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Resturlaub</span>
                                <span className="font-medium text-emerald-500">{user?.remaining_vacation_days} Tage</span>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default SettingsPage;
